import {TargetGroup} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/target_group.types';
import {GetGenderSwitchResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/coupons/v1/landingpage/service.types';
import {Link, useHistory} from '@aboutyou/router';
import {useTrackFeatureInViewImpression} from '@aboutyou/tracking';
import {Fade} from '@aboutyou/transitions';
import {
  COLORS,
  FONT_SIZES,
  FONT_WEIGHTS,
  desktopAndUp,
  supportsHover,
  tabletAndUp,
} from '@aboutyou/ui-components';
import React, {useEffect} from 'react';
import styled, {css} from 'styled-components';
import {
  convertTargetGroupToGender,
  useSelectedTargetGroup,
} from '../../shared-deprecated/useSelectedTargetGroup';
import {useTargetGroupFromUrlParam} from '../../shared-deprecated/useTargetGroupIdentifier';
import {useGetGenderSwitchTargetUrl} from './utils/useGetGenderSwitchTargetUrl';
import {useNotifyForSelectedGenderNotAvailable} from './utils/useNotifyForSelectedGenderNotAvailable';

export const StreamGenderSwitch: React.FC<
  GetGenderSwitchResponse & {
    className?: string;
    adjustGenderQueryParam: boolean;
  }
> = ({
  className,
  genderCategories,
  selectedGenderNotAvailable,
  adjustGenderQueryParam = false,
}) => {
  const getGenderSwitchTargetUrl = useGetGenderSwitchTargetUrl();
  const history = useHistory();

  const targetGroup = useTargetGroupFromUrlParam();
  const {targetGroup: selectedTargetGroup, switchTargetGroup} =
    useSelectedTargetGroup();

  useNotifyForSelectedGenderNotAvailable(selectedGenderNotAvailable);

  const {ref} = useTrackFeatureInViewImpression<HTMLDivElement>({
    name: 'gender_switch',
  });

  // When opening a CLP which is multi-gendered, we want to add the gender query param to the URL
  // so that when the user switches the gender using GenderSwitch, the URL is updated accordingly and the browser Back navigation works as expected.
  useEffect(() => {
    if (!adjustGenderQueryParam) {
      return;
    }

    if (!targetGroup && genderCategories.length) {
      history.replace({
        pathname: history.location.pathname,
        search: `?gender=${convertTargetGroupToGender(selectedTargetGroup)}`,
      });
    }
  }, [
    adjustGenderQueryParam,
    genderCategories.length,
    history,
    selectedTargetGroup,
    targetGroup,
  ]);

  // When the url is updated with the `gender` query param in the useEffect above, we want to update the global targetGroup state
  useEffect(() => {
    if (!adjustGenderQueryParam) {
      return;
    }

    if (targetGroup) {
      switchTargetGroup(targetGroup || TargetGroup.FEMALE);
    }
  }, [targetGroup]);

  if (genderCategories.length <= 1) {
    return null;
  }

  return (
    <div ref={ref}>
      <Fade mounted={true} duration={300}>
        <Wrapper data-testid="streamGenderSwitch" className={className}>
          {genderCategories.map((labelCategory, index) => {
            const resolvedTargetGroup = adjustGenderQueryParam
              ? targetGroup ?? selectedTargetGroup
              : selectedTargetGroup;

            const isActive = genderCategories.find(
              item => item.category.targetGroup === resolvedTargetGroup,
            )
              ? labelCategory.category.targetGroup === resolvedTargetGroup
              : index === 0; // fallback to the first available gender

            const gender = convertTargetGroupToGender(
              labelCategory.category.targetGroup,
            );

            return (
              <Gender
                key={labelCategory.category.id}
                type="primary"
                data-active={isActive}
                data-show-separator={index > 0}
                to={getGenderSwitchTargetUrl(
                  labelCategory.category,
                  adjustGenderQueryParam ? gender : undefined,
                )}
              >
                {labelCategory.label}
              </Gender>
            );
          })}
        </Wrapper>
      </Fade>
    </div>
  );
};

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 5px 0;
  border-top: 1px solid ${COLORS.Grey3};
  border-bottom: 1px solid ${COLORS.Grey3};
  gap: 20px;

  ${tabletAndUp(
    css`
      border: 1px solid ${COLORS.Grey1};
      padding: 0;
      border-radius: 110px;
      overflow: hidden;
      gap: 0;
    `,
  )}

  ${desktopAndUp(
    css`
      margin-left: 16px;
    `,
  )}
`;

const Gender = styled(Link)`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 52px;
  height: 26px;
  color: ${COLORS.Grey2};
  font-size: ${FONT_SIZES.px12};
  font-weight: ${FONT_WEIGHTS.bold};
  line-height: 18px;
  transition: color 0.5s;
  text-decoration: none;
  text-transform: uppercase;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -5px;
    margin: auto;
    width: 24px;
    height: 2px;
    background: ${COLORS.Black};
    transform: scale(0);
    transition: transform 0.5s ease;
  }

  &[data-active='true'] {
    color: ${COLORS.Black};
    font-weight: ${FONT_WEIGHTS.heavy};

    &::after {
      transform: scale(1);
    }
  }

  ${supportsHover(css`
    &:hover {
      color: ${COLORS.Black};
      font-weight: ${FONT_WEIGHTS.heavy};

      &::after {
        transform: scale(1);
      }
    }
  `)}

  ${tabletAndUp(css`
    min-width: 80px;
    height: 22px;
    padding-inline: 12px;
    color: ${COLORS.Grey1};

    &[data-active='true'] {
      background: ${COLORS.Black};
      color: ${COLORS.White};

      &:after {
        display: none;
      }
    }

    &[data-show-separator='true'] {
      &:after {
        background: ${COLORS.Grey1};
        width: 1px;
        height: 100%;
        bottom: unset;
        right: 0;
        transform: unset;
        margin: unset;
      }
    }
  `)}
`;
